import {
    BrowserRouter,
    Switch,
    Route
} from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useEffect, useState } from "react";
import axios from "axios";
import MemberService from "./services/MemberService";
import DynamicResourcesUtils from "./services/DynamicResourcesUtils";
import NewMemberEnrollmentView from "./views/NewMemberEnrollmentView";
import AuthenticateWithTokenView from "./views/AuthenticateWithTokenView";
import HomeView from "./views/HomeView";
import LoginView from "./views/LoginView";
import PrivateRoute from "./components/PrivateRoute";
import ScrollToTop from "./components/ScrollToTop";
import AuthProvider from "./providers/AuthProvider";
import NotificationProvider from "./providers/NotificationProvider";
import FastTrackEnrollmentView from "./views/FastTrackEnrollmentView";
import CaptchaProtectedRoute from "./components/CaptchaProtectedRoute";
import HttpService, { getAppToken } from "./services/HttpService";

import "./App.css";

// REF.: https://material-ui.com/customization/palette/
const theme = createTheme({
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontSize: '0.875rem',
                    lineHeight: 1.43,
                    letterSpacing: '0.01071em',
                },
            },
        },
    },
    palette: {
        primary: {
            main: "#002b52",
            // main: "#022038",
        },
        secondary: {
        //     light: "#0066ff",
            main: "#ff9000",
        },
        contrastThreshold: 3,
        tonalOffset: 0.2,
    },
});

function App() {
    const [isInitialized, setIsInitialized] = useState(false);
    
    // First initialize authentication token
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Ensure we have a valid app token before proceeding
                await getAppToken();
                setIsInitialized(true);
            } catch (error) {
                console.error('Failed to initialize authentication:', error);
                // Set initialized anyway so the app can load, we'll retry token acquisition later
                setIsInitialized(true);
            }
        };
        
        initializeAuth();
    }, []);
    
    // Then load dynamic resources after authentication is initialized
    useEffect(() => {
        // Only proceed if auth is initialized
        if (!isInitialized) return;
        
        const source = axios.CancelToken.source();
        
        const loadResources = async () => {
            try {
                // Get a fresh token before loading resources
                try {
                    await getAppToken();
                } catch (tokenError) {
                    console.error('Error refreshing token before loading resources:', tokenError);
                    // Continue anyway, the resource utils will retry if needed
                }
                
                // Fetch and update organization name
                MemberService.getOrganizationName(source.token)
                    .then(data => {
                        if (data && window.updateOrganizationNameInDOM) {
                            window.updateOrganizationNameInDOM(data);
                        }
                    })
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error fetching organization name:', error);
                        }
                    });
                            
                // Fetch and update portal title
                MemberService.getPortalTitle(source.token)
                    .then(data => {
                        if (data && window.updatePortalTitleInDOM) {
                            window.updatePortalTitleInDOM(data);
                        }
                    })
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error fetching portal title:', error);
                        }
                    });
                
                // Fetch and update favicon
                await DynamicResourcesUtils.updateFavicon(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating favicon:', error);
                        }
                    });
                    
                // Fetch and update background image
                await DynamicResourcesUtils.updateBackgroundImage(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating background image:', error);
                        }
                    });
                    
                // Fetch and update portal logo
                await DynamicResourcesUtils.updatePortalLogoNoBackground(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating portal logo:', error);
                        }
                    });
                    
                // Fetch and update icon
                await DynamicResourcesUtils.updateLogoIcon(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating logo icon:', error);
                        }
                    });

                // Fetch and update divider color
                await DynamicResourcesUtils.updatePortalDividerColor(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating portal divider color:', error);
                        }
                    });

                // Fetch and update panel color
                await DynamicResourcesUtils.updatePortalPanelColor(source.token)
                    .catch(error => {
                        if (!axios.isCancel(error)) {
                            console.error('Error updating portal panel color:', error);
                        }
                    });

            } catch (error) {
                console.error('Error loading resources:', error);
            }
        };
        
        // Load resources immediately
        loadResources();
        
        // Also attempt to reload when the window regains focus (user returns to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('Document became visible, refreshing resources');
                loadResources();
            }
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            source.cancel();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isInitialized]);

    return (
        <ThemeProvider theme={theme}>
            <NotificationProvider>
                <AuthProvider>
                    <CssBaseline />
                    <BrowserRouter basename={process.env.REACT_APP_BASENAME}>
                        <ScrollToTop />
                        <Switch>
                            <Route exact path="/authenticate">
                                <AuthenticateWithTokenView />
                            </Route>
                            <Route path={"/(signin|registration|recovery)"}>
                                <LoginView />
                            </Route>
                            <CaptchaProtectedRoute path="/fasttrack/:preSelectedLocalUnionId(\d+)?">
                                <div className="fasttrack-enrollment-container">
                                    <FastTrackEnrollmentView />
                                </div>
                            </CaptchaProtectedRoute>
                            <CaptchaProtectedRoute path="/newEnrollment/:preSelectedLocalUnionId(\d+)?" subroutesToBypass={["/sign"]}>
                                <div className="enrollment-container">
                                    <NewMemberEnrollmentView />
                                </div>
                            </CaptchaProtectedRoute>
                            <PrivateRoute path="/*">
                                <HomeView />
                            </PrivateRoute>
                        </Switch>
                    </BrowserRouter>
                </AuthProvider>
            </NotificationProvider>
        </ThemeProvider>
    );
}

export default App;
